'use client';
import React, { useEffect, useMemo, useState } from 'react';
import { useLocale } from 'use-intl';
import Image from '../../../image';
import SectionTitle from './SectionTitle';
import styles from './ReportsSection.module.css';
import type {
  ConferenceSite,
  IndustryReportItem,
  IndustryReportListResp,
} from '../types';

interface ReportsSectionProps {
  data?: IndustryReportListResp;
  title?: string;
  site?: ConferenceSite;
}

// PC 版每页完整展示 4 条，额外渲染下一条作为半露出预览，对齐 Figma 4.5 张版式。
const REPORT_FULL_COLUMN_COUNT = 4;
const REPORT_RENDER_COUNT = REPORT_FULL_COLUMN_COUNT + 1;

const normalizeReportTitle = (title: string) => title.replace(/\\n/g, '\n');

const getReportTitle = (item: IndustryReportItem, isZh: boolean) =>
  normalizeReportTitle(
    (isZh ? item.TitleCN || item.TitleEN : item.TitleEN || item.TitleCN) || '-'
  );

const getAccessibleReportTitle = (title: string) =>
  title.replace(/\s+/g, ' ').trim();

const getReportURL = (item: IndustryReportItem) =>
  item.PreviewURL || item.ReportFile || item.ReportURL;

function ReportPagerArrow({ direction }: { direction: 'left' | 'right' }) {
  return (
    <svg
      width="40"
      height="40"
      viewBox="0 0 40 40"
      fill="none"
      aria-hidden="true"
    >
      {direction === 'left' ? (
        <>
          <circle
            cx="20"
            cy="20"
            r="19.4"
            stroke="currentColor"
            strokeWidth="1.2"
          />
          <path
            d="M22 16L18 20L22 24"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </>
      ) : (
        <>
          <circle
            cx="20"
            cy="20"
            r="19.4"
            stroke="currentColor"
            strokeWidth="1.2"
          />
          <path
            d="M18 16L22 20L18 24"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </>
      )}
    </svg>
  );
}

function ReadReportArrow() {
  return (
    <svg
      className={styles.readReportArrow}
      width="14"
      height="12"
      viewBox="0 0 14 12"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M7.51 0.87L8.33 0L13.8 5.79C13.91 5.9 13.91 6.09 13.8 6.2L8.33 12L7.51 11.12L11.76 6.61H0V5.38H11.76L7.51 0.87Z"
        fill="currentColor"
        fillRule="evenodd"
      />
    </svg>
  );
}

/**
 * 行业报告组件
 */
export default function ReportsSection({
  data,
  title,
  site = 'zh',
}: ReportsSectionProps) {
  const locale = useLocale();
  const isZh = locale === 'zh';
  const isOverseaSite = site === 'en';
  const titleClassName = `${styles.title} ${!isZh ? styles.englishTitle : ''}`;
  const list = useMemo(() => data?.list || [], [data]);
  const [page, setPage] = useState(1);
  const pageCount = Math.max(
    1,
    Math.ceil(list.length / REPORT_FULL_COLUMN_COUNT)
  );
  const safePage = Math.min(page, pageCount);
  const currentList = useMemo(() => {
    // 每页推进 4 条，半露出的第 5 条会在下一页作为完整卡片展示，避免后端数据只露半张。
    const start = (safePage - 1) * REPORT_FULL_COLUMN_COUNT;
    return list.slice(start, start + REPORT_RENDER_COUNT);
  }, [list, safePage]);

  useEffect(() => {
    setPage(1);
  }, [list]);

  const handlePrev = () => {
    setPage((current) => Math.max(1, current - 1));
  };

  const handleNext = () => {
    setPage((current) => Math.min(pageCount, current + 1));
  };

  const renderReportCard = (item: IndustryReportItem) => {
    const titleText = getReportTitle(item, isZh);
    const accessibleTitleText = getAccessibleReportTitle(titleText);
    const url = getReportURL(item);
    const reportBodyClassName = isOverseaSite
      ? `${styles.reportBody} ${styles.reportBodyOversea}`
      : styles.reportBody;
    const reportTitleClassName = isOverseaSite
      ? styles.reportTitleOversea
      : undefined;
    return (
      <button
        key={item.ReportID || item.ID}
        className={styles.reportCard}
        onClick={() => url && window.open(url, '_blank')}
        disabled={!url}
        type="button"
        aria-label={`${isZh ? '阅读报告' : 'Read report'}：${accessibleTitleText}`}
      >
        <div className={styles.coverWrap}>
          {item.CoverImage ? (
            <Image
              src={item.CoverImage}
              alt={accessibleTitleText}
              className={styles.cover}
              loading="lazy"
            />
          ) : (
            <div className={styles.coverFallback}>-</div>
          )}
        </div>
        <div className={reportBodyClassName}>
          <h3 className={reportTitleClassName} title={accessibleTitleText}>
            {titleText}
          </h3>
          <span className={styles.openButton}>
            {isZh ? '阅读报告' : 'Read report'}
            <ReadReportArrow />
          </span>
        </div>
      </button>
    );
  };

  if (!list.length) {
    return null;
  }

  return (
    <section id="reports" className={styles.section}>
      <div className={styles.container}>
        <SectionTitle className={titleClassName}>{title}</SectionTitle>
        <div className={styles.reportGridWrap}>
          <div className={styles.reportGrid}>
            {currentList.map(renderReportCard)}
          </div>
          {pageCount > 1 && safePage < pageCount ? (
            <div className={styles.reportGridFade} aria-hidden="true" />
          ) : null}
        </div>
        {/* 移动端设计要求行业报告横滑浏览，使用完整列表展示，不受 PC 分页页码影响。 */}
        <div className={styles.mobileReportScroller}>
          <div className={styles.mobileReportTrack}>
            {list.map(renderReportCard)}
          </div>
        </div>
        {pageCount > 1 && (
          <div
            className={styles.pager}
            aria-label={isZh ? '行业报告翻页' : 'Report pagination'}
          >
            <button
              className={styles.pageButton}
              onClick={handlePrev}
              disabled={page === 1}
              type="button"
              aria-label={isZh ? '上一页' : 'Previous page'}
            >
              <ReportPagerArrow direction="left" />
            </button>
            <span className={styles.pageText}>
              {safePage}/{pageCount}
            </span>
            <button
              className={styles.pageButton}
              onClick={handleNext}
              disabled={safePage === pageCount}
              type="button"
              aria-label={isZh ? '下一页' : 'Next page'}
            >
              <ReportPagerArrow direction="right" />
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
